import { useMemo } from 'react'
import { useCompanyProductOverrides, useInternalProductInfo, usePinduoduoOrderFacts, useStoreCostProfiles } from './platform'
import { pinduoduoIsPaidOrderOutcome, pinduoduoPaidOrderItemCount, type PddDailySkuOrderRecord } from './pinduoduo'
import { estimatePinduoduoProfit, type PinduoduoProfitEstimate } from './pinduoduoProfit'
import { pinduoduoStatisticsWindow, type PinduoduoCustomRange, type PinduoduoStatisticsPeriod } from './pinduoduoStatistics'
import type { CompanyProductOverrideRecord, InternalProductInfoRecord } from './types'

export interface PddCommerceAggregate {
  revenue: number
  soldItemCount: number
  profit: PinduoduoProfitEstimate | null
}

export const pddProductSkuKey = (productId: string, skuCode: string) => `${productId}\u0000${skuCode.trim()}`

const commerceAggregate = (
  rows: PddDailySkuOrderRecord[],
  canEstimateProfit: boolean,
  profile: Parameters<typeof estimatePinduoduoProfit>[1],
  companyRecordsByCode: Map<string, InternalProductInfoRecord[]>,
  companyOverridesByCode: Map<string, CompanyProductOverrideRecord>,
): PddCommerceAggregate => ({
  revenue: rows.reduce((sum, row) => sum + row.merchantReceivable, 0),
  soldItemCount: rows.reduce((sum, row) => sum + row.soldItemCount, 0),
  profit: canEstimateProfit ? estimatePinduoduoProfit(rows, profile, companyRecordsByCode, companyOverridesByCode) : null,
})

export function usePinduoduoRecent7ConfirmedItemCounts() {
  const orders = usePinduoduoOrderFacts()
  const window = useMemo(() => pinduoduoStatisticsWindow(orders.data, 'last7'), [orders.data])
  const byProductSku = useMemo(() => {
    const result = new Map<string, number>()
    if (!window?.complete) return result
    for (const row of orders.data?.dailySku || []) {
      if (row.outcome !== 'confirmed' || row.date < window.startDate || row.date > window.endDate) continue
      const key = pddProductSkuKey(row.productId, row.skuCode)
      result.set(key, (result.get(key) || 0) + row.soldItemCount)
    }
    return result
  }, [orders.data, window])
  return { byProductSku, window, loading: orders.loading, error: orders.error }
}

export function usePinduoduoCommerceMetrics(statisticsPeriod: PinduoduoStatisticsPeriod, customRange: PinduoduoCustomRange) {
  const orders = usePinduoduoOrderFacts()
  const company = useInternalProductInfo()
  const companyOverrides = useCompanyProductOverrides()
  const storeCosts = useStoreCostProfiles()
  const window = useMemo(() => pinduoduoStatisticsWindow(orders.data, statisticsPeriod, customRange), [customRange.endDate, customRange.startDate, orders.data, statisticsPeriod])
  const windowEnd = window?.endDate || ''
  const windowStart = window?.startDate || ''
  const windowRows = useMemo(() => (orders.data?.dailySku || []).filter((row) =>
    row.date >= windowStart && row.date <= windowEnd,
  ), [orders.data, windowEnd, windowStart])
  const confirmedRows = useMemo(() => windowRows.filter((row) => row.outcome === 'confirmed'), [windowRows])
  const companyRecordsByCode = useMemo(() => {
    const result = new Map<string, InternalProductInfoRecord[]>()
    for (const record of company.data?.records || []) {
      const code = record.productCode.trim()
      const records = result.get(code) || []
      records.push(record)
      result.set(code, records)
    }
    return result
  }, [company.data])
  const companyOverridesByCode = useMemo(() => new Map(
    (companyOverrides.data?.records || []).map((record) => [record.companyVariantId.trim(), record]),
  ), [companyOverrides.data])
  const profile = storeCosts.data?.stores.find((item) =>
    item.platformId === 'pinduoduo' && item.storeId === orders.data?.meta.storeId,
  )
  const canEstimateProfit = Boolean(company.data && companyOverrides.data && storeCosts.data && profile)
  const rowsByProductId = useMemo(() => {
    const grouped = new Map<string, PddDailySkuOrderRecord[]>()
    for (const row of confirmedRows) {
      const rows = grouped.get(row.productId)
      if (rows) rows.push(row)
      else grouped.set(row.productId, [row])
    }
    return grouped
  }, [confirmedRows])
  const rowsByProductSku = useMemo(() => {
    const grouped = new Map<string, PddDailySkuOrderRecord[]>()
    for (const row of confirmedRows) {
      const key = pddProductSkuKey(row.productId, row.skuCode)
      const rows = grouped.get(key)
      if (rows) rows.push(row)
      else grouped.set(key, [row])
    }
    return grouped
  }, [confirmedRows])
  const orderedItemCountByProductId = useMemo(() => {
    const grouped = new Map<string, number>()
    for (const row of windowRows) {
      if (!pinduoduoIsPaidOrderOutcome(row.outcome)) continue
      grouped.set(row.productId, (grouped.get(row.productId) || 0) + row.soldItemCount)
    }
    return grouped
  }, [windowRows])
  const paidRowsByProductSku = useMemo(() => {
    const grouped = new Map<string, PddDailySkuOrderRecord[]>()
    for (const row of windowRows) {
      if (!pinduoduoIsPaidOrderOutcome(row.outcome)) continue
      const key = pddProductSkuKey(row.productId, row.skuCode)
      const rows = grouped.get(key)
      if (rows) rows.push(row)
      else grouped.set(key, [row])
    }
    return grouped
  }, [windowRows])
  const orderedItemCountByProductSku = useMemo(() => new Map(
    [...paidRowsByProductSku].map(([key, rows]) => [key, pinduoduoPaidOrderItemCount(rows)]),
  ), [paidRowsByProductSku])
  const orderedItemCount = useMemo(() => pinduoduoPaidOrderItemCount(windowRows), [windowRows])
  const overall = useMemo(() => commerceAggregate(
    confirmedRows, canEstimateProfit, profile, companyRecordsByCode, companyOverridesByCode,
  ), [canEstimateProfit, companyOverridesByCode, companyRecordsByCode, confirmedRows, profile])
  const byProductId = useMemo(() => new Map([...rowsByProductId].map(([key, rows]) => [key, commerceAggregate(
    rows, canEstimateProfit, profile, companyRecordsByCode, companyOverridesByCode,
  )])), [canEstimateProfit, companyOverridesByCode, companyRecordsByCode, profile, rowsByProductId])
  const byProductSku = useMemo(() => new Map([...rowsByProductSku].map(([key, rows]) => [key, commerceAggregate(
    rows, canEstimateProfit, profile, companyRecordsByCode, companyOverridesByCode,
  )])), [canEstimateProfit, companyOverridesByCode, companyRecordsByCode, profile, rowsByProductSku])
  const unresolvedRows = windowRows.filter((row) => row.outcome === 'paid_unresolved')
  const unresolvedOrderCount = unresolvedRows.reduce((sum, row) => sum + row.orderCount, 0)
  const unresolvedItemCount = unresolvedRows.reduce((sum, row) => sum + row.soldItemCount, 0)
  const reconciliationNote = (orders.data?.meta.reconciliation?.state === 'incomplete'
    ? '下单为现存订单去重后的已知数量，缺原单售后未补入，不代表平台全量。' : '') + (unresolvedOrderCount > 0
    ? `当前统计范围有 ${unresolvedOrderCount} 单 / ${unresolvedItemCount} 件已支付待核实，已计入下单件数，未计入确认成交及利润；当前利润仅代表已确认成交部分。`
    : '')
  const loading = orders.loading || company.loading || companyOverrides.loading || storeCosts.loading
  const error = orders.error || company.error || companyOverrides.error || storeCosts.error
    || (!loading && orders.data && storeCosts.data && !profile ? '未找到当前拼多多店铺成本配置' : '')
  return {
    byProductId,
    byProductSku,
    orderedItemCount,
    reconciliationNote,
    orderedItemCountByProductId,
    orderedItemCountByProductSku,
    paidRowsByProductSku,
    rowsByProductSku,
    companyRecordsByCode,
    companyOverridesByCode,
    profile,
    overall,
    window,
    loading,
    error,
  }
}
